import {
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  InputNumber,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { hrDepthApi, type SalaryAdvance } from '@/api/hr-depth';
import { financeApi } from '@/api/finance';
import { hrApi } from '@/api/hr';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

interface FormShape {
  staffId: number;
  advanceDate: Dayjs;
  amount: number;
  repaymentMonths: number;
  bankAccountId: number;
  remarks?: string;
}

export function SalaryAdvancesPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canApprove = useAuthStore((s) => s.hasAuthority('HR_MGMT:APPROVE'));

  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'month'), dayjs()]);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isFetching } = useQuery({
    queryKey: ['hr', 'salary-advances', range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD')],
    queryFn: () => hrDepthApi.listAdvances(range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD')),
  });

  const { data: staffPage } = useQuery({
    queryKey: ['hr', 'staff', 'active', 'all'],
    queryFn: () => hrApi.searchStaff({ active: true, size: 500 }),
    staleTime: 60_000,
  });
  const staff = staffPage?.rows ?? [];

  const banksQuery = useQuery({
    queryKey: ['finance', 'bank-accounts', 'active'],
    queryFn: () => financeApi.listBankAccounts(),
    staleTime: 5 * 60 * 1000,
  });
  const banks = (banksQuery.data ?? []).filter((b) => b.active);

  const add = useMutation({
    mutationFn: (v: FormShape) =>
      hrDepthApi.addAdvance({
        staffId: v.staffId,
        advanceDate: v.advanceDate.format('YYYY-MM-DD'),
        amount: Number(v.amount).toFixed(2),
        repaymentMonths: v.repaymentMonths,
        bankAccountId: v.bankAccountId,
        remarks: v.remarks?.trim() || undefined,
      }),
    onSuccess: () => {
      message.success('Advance disbursed · DR Staff Advance · CR Cash/Bank');
      qc.invalidateQueries({ queryKey: ['hr', 'salary-advances'] });
      setAdding(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const watchedAmount = Form.useWatch('amount', form);
  const watchedMonths = Form.useWatch('repaymentMonths', form);
  const emiPreview = watchedAmount && watchedMonths
    ? Number(watchedAmount) / Number(watchedMonths)
    : null;

  const totalOutstanding = (data ?? []).reduce((s, a) => s + Number(a.outstanding), 0);

  const columns: TableProps<SalaryAdvance>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'advanceDate',
      width: 120,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    { title: 'Staff', dataIndex: 'staffName' },
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 130,
      align: 'right',
      render: (a: string) => `PKR ${PKR(a)}`,
    },
    {
      title: 'EMI',
      width: 140,
      align: 'right',
      render: (_, row) => `PKR ${PKR(row.monthlyEmi)} × ${row.repaymentMonths}`,
    },
    {
      title: 'Recovered',
      dataIndex: 'recoveredAmount',
      width: 120,
      align: 'right',
      render: (v: string) => `PKR ${PKR(v)}`,
    },
    {
      title: 'Outstanding',
      dataIndex: 'outstanding',
      width: 140,
      align: 'right',
      render: (v: string, row) =>
        row.fullyRecovered ? (
          <Tag color="success">Fully recovered</Tag>
        ) : (
          <Tag color="warning" style={{ fontWeight: 600 }}>
            PKR {PKR(v)}
          </Tag>
        ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      render: (r: string | null) => r ?? '—',
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / HR"
        title="Salary advances"
        description="Disburse advances and recover via monthly EMI in payroll. Recovery is automatic on finalization."
        count={data ? { value: data.length, label: 'in range' } : undefined}
        actions={
          canApprove ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => {
              form.resetFields();
              form.setFieldsValue({ advanceDate: dayjs() });
              setAdding(true);
            }}>
              Disburse advance
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        {data && data.length > 0 && (
          <Tag color="warning" style={{ fontSize: 13 }}>
            Total outstanding · PKR {PKR(totalOutstanding)}
          </Tag>
        )}
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No advances in this range" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            size="middle"
            loading={isFetching}
            columns={columns}
            dataSource={data ?? []}
            pagination={{ pageSize: 25 }}
          />
        </Card>
      )}

      <Modal
        title="Disburse salary advance"
        open={adding}
        onCancel={() => setAdding(false)}
        onOk={() => form.submit()}
        confirmLoading={add.isPending}
        okText="Disburse"
        destroyOnHidden
      >
        {adding && (
          <Form form={form} layout="vertical" onFinish={(v) => add.mutate(v)}>
            <Form.Item label="Staff" name="staffId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={staff.map((s) => ({ value: s.id, label: `${s.firstName} ${s.lastName}` }))}
              />
            </Form.Item>
            <Form.Item label="Date" name="advanceDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Amount (PKR)"
                name="amount"
                rules={[{ required: true, type: 'number', min: 0.01 }]}
                style={{ flex: 1 }}
              >
                <InputNumber style={{ width: '100%' }} min={0} step={5000} />
              </Form.Item>
              <Form.Item
                label="Repay over (months)"
                name="repaymentMonths"
                rules={[{ required: true, type: 'number', min: 1, max: 60 }]}
                style={{ width: 200 }}
              >
                <InputNumber style={{ width: '100%' }} min={1} max={60} step={1} />
              </Form.Item>
            </Space.Compact>
            {emiPreview != null && (
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                Monthly EMI: PKR {PKR(emiPreview.toFixed(2))}
              </Typography.Text>
            )}
            <Form.Item
              label="Pay from"
              name="bankAccountId"
              rules={[{ required: true, message: 'Required — drives the GL posting' }]}
              tooltip="Posts a debit to Staff Advance asset and a credit to this account."
            >
              <Select
                loading={banksQuery.isLoading}
                placeholder="Cash drawer or bank account"
                options={banks.map((b) => ({
                  value: b.id,
                  label: `${b.accountName}${b.bankName ? ` · ${b.bankName}` : ''}${b.cash ? ' (cash)' : ''}`,
                }))}
              />
            </Form.Item>
            <Form.Item label="Remarks" name="remarks" rules={[{ max: 1000 }]}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
